import { mascaraCPF } from '@/pages/inscricao/utils';
import { codigoCurto } from '@/pages/pedido/utils';

interface DadosTicket {
  evento: string;
  dataHora: string;
  local: string | null;
  portador: string;
  participacao: string;
  qrCanvas: HTMLCanvasElement;
  /** CPF do portador. Quando vem, ocupa a linha de baixo (como no ingresso do pedido). */
  cpf?: string | null;
  /** Código completo do ingresso. Na imagem só aparece o pedaço curto; o resto vive no QR. */
  codigo?: string | null;
}

const LARGURA = 600;
const ALTURA = 800;
const LARGURA_TEXTO = 540;

// Diminui a fonte até o texto caber na largura, em vez de vazar para fora.
// O modelo usa %d como espaço reservado do tamanho (ex.: 'bold %d sans-serif').
function escreverAjustado(
  ctx: CanvasRenderingContext2D,
  texto: string,
  modelo: string,
  tamanhoBase: number,
  larguraMax: number,
): void {
  let tamanho = tamanhoBase;
  ctx.font = modelo.replace('%d', `${tamanho}px`);
  while (tamanho > 12 && ctx.measureText(texto).width > larguraMax) {
    tamanho -= 2;
    ctx.font = modelo.replace('%d', `${tamanho}px`);
  }
}

// Desenha o ingresso (600×800) a partir do QR já renderizado em tela.
// Quem não passar `cpf` nem `codigo` sai exatamente como sempre saiu.
export function gerarTicketIngresso(dados: DadosTicket): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = LARGURA;
  canvas.height = ALTURA;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, LARGURA, ALTURA);
  ctx.textAlign = 'center';

  ctx.fillStyle = '#111827';
  ctx.font = 'bold 34px sans-serif';
  ctx.fillText(dados.evento, LARGURA / 2, 70);

  ctx.fillStyle = '#4b5563';
  ctx.font = '22px sans-serif';
  ctx.fillText(dados.dataHora, LARGURA / 2, 115);

  if (dados.local) {
    ctx.fillStyle = '#4b5563';
    escreverAjustado(ctx, dados.local, '%d sans-serif', 20, LARGURA_TEXTO);
    ctx.fillText(dados.local, LARGURA / 2, 150);
  }

  const qrSize = 380;
  ctx.drawImage(dados.qrCanvas, (LARGURA - qrSize) / 2, 190, qrSize, qrSize);

  ctx.fillStyle = '#111827';
  escreverAjustado(ctx, dados.portador, 'bold %d sans-serif', 28, LARGURA_TEXTO);
  ctx.fillText(dados.portador, LARGURA / 2, 610);

  if (dados.cpf) {
    ctx.fillStyle = '#4b5563';
    ctx.font = '22px sans-serif';
    ctx.fillText(`CPF ${mascaraCPF(dados.cpf)}`, LARGURA / 2, 650);
  } else if (dados.participacao) {
    ctx.fillStyle = '#4b5563';
    ctx.font = '22px sans-serif';
    ctx.fillText(dados.participacao, LARGURA / 2, 650);
  }

  if (dados.codigo) {
    ctx.fillStyle = '#4b5563';
    ctx.font = '20px monospace';
    ctx.fillText(`Ingresso · ${codigoCurto(dados.codigo)}`, LARGURA / 2, 690);
  }

  return canvas;
}

// Salva/compartilha a imagem do ingresso. No celular moderno abre o menu de
// compartilhar (salva na galeria ou manda direto); no desktop baixa o arquivo.
export async function salvarOuCompartilhar(
  canvas: HTMLCanvasElement,
  nomeArquivo: string,
  texto: string,
): Promise<void> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return;
  const arquivo = new File([blob], nomeArquivo, { type: 'image/png' });

  if (navigator.canShare?.({ files: [arquivo] })) {
    try {
      await navigator.share({ files: [arquivo], title: nomeArquivo, text: texto });
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
    }
  }

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = nomeArquivo;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}